import { useState, useEffect } from 'react';
import { Megaphone, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

interface NotificationRow {
  id: string;
  title: string;
  body_html: string;
  created_at: string;
}

/**
 * Top-of-app announcement banner. Shows the newest active, non-dismissed
 * notification; clicking opens a modal listing all of them, each dismissable.
 * Rendered inside Layout for every authenticated user. Non-critical: any fetch
 * error is swallowed so it can never break the app chrome.
 */
export function NotificationBanner() {
  const { t } = useTranslation('common');
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await apiClient.get<NotificationRow[]>('/api/notifications/active');
        if (!cancelled) setItems(rows ?? []);
      } catch {
        /* silent */
      }
    };
    load();
    const id = setInterval(load, 120000); // pick up newly-published notifications
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (items.length === 0) setOpen(false);
  }, [items.length]);

  const dismiss = async (id: string) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try {
      await apiClient.post(`/api/notifications/${id}/dismiss`, {});
    } catch {
      /* best-effort; it's already gone from the UI */
    }
  };

  if (items.length === 0) return null;
  const newest = items[0];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm hover:bg-indigo-700 transition-colors"
      >
        <Megaphone size={16} className="shrink-0" />
        <span className="truncate font-medium">{newest.title}</span>
        {items.length > 1 && (
          <span className="ml-1 shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-xs">{/* theme-allow: white-on-accent count pill over the indigo banner */}
            {t('announcements.more', { count: items.length - 1 })}
          </span>
        )}
        <span className="ml-auto shrink-0 text-xs underline">{t('announcements.view')}</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-surface rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-line">
              <h2 className="text-base font-bold text-theme-primary flex items-center gap-2">
                <Megaphone size={18} /> {t('announcements.title')}
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-lg text-theme-secondary hover:bg-subtle"
                aria-label={t('announcements.close')}
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4 space-y-5">
              {items.map((n) => (
                <div key={n.id} className="border-b border-line pb-4 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-semibold text-theme-primary">{n.title}</h3>
                    <button
                      onClick={() => dismiss(n.id)}
                      className="shrink-0 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                    >
                      {t('announcements.dismiss')}
                    </button>
                  </div>
                  <div
                    className="mt-2 max-w-none text-sm text-theme-secondary [&_h3]:font-semibold [&_h3]:text-theme-primary [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                    dangerouslySetInnerHTML={{ __html: n.body_html }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
